import { t } from 'i18next';
import _ from 'lodash';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { reindexOrderInLaneForReservation } from '@/trendData/trendLaneReservation.utilities';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { BaseItem, ConditionItem } from '@/trendData/trendData.types';
import type { WithProperty } from '@/utilities.types';

/**
 * Represents a condition layout update with positioning information.
 * Used when updating overlay/order-in-lane values for conditions in a lane.
 */
export interface ConditionLayoutUpdate {
  id: string;
  lane?: number;
  overlay?: number | undefined;
  orderInLane?: number | undefined;
  orderInLanePriority?: number | undefined;
}

/**
 * Get overlay options for display in details pane when conditions share a lane
 */
export function getOverlayOptions(lane: number, items: ConditionItem[]): ReactSelectOption[] {
  const itemsInLane = items.filter((condition) => condition.lane === lane);
  if (itemsInLane.length < 2) {
    return [];
  }

  const laneNumbers = _.range(1, itemsInLane.length + 1);
  const options: ReactSelectOption[] = laneNumbers.map((laneNumber, index) => ({
    text: `${laneNumber}${
      (index === 0 && ` ${t('DETAILS_PANE.SEND_TO_BACK')}`) ||
      (index === laneNumbers.length - 1 && ` ${t('DETAILS_PANE.SEND_TO_FRONT')}`) ||
      ''
    }`,
    value: laneNumber,
  }));

  const hasOverlay = itemsInLane.some((condition) => condition.overlay !== undefined);
  if (hasOverlay) {
    return options.concat([{ text: 'DETAILS_PANE.DO_NOT_OVERLAY', value: undefined }]);
  }

  return options;
}

/**
 * Get order-in-lane options for display in details pane when conditions share a lane
 */
export function getOrderInLaneOptions(lane: number, items: ConditionItem[]): ReactSelectOption[] {
  const itemsInLane = items.filter((condition) => condition.lane === lane);
  if (itemsInLane.length < 2) {
    return [];
  }

  const laneNumbers = _.range(1, itemsInLane.length + 1);
  const options: ReactSelectOption[] = laneNumbers.map((laneNumber, index) => ({
    text: `${laneNumber}${
      (index === 0 && ` ${t('DETAILS_PANE.ORDER_TOP')}`) ||
      (index === laneNumbers.length - 1 && ` ${t('DETAILS_PANE.ORDER_BOTTOM')}`) ||
      ''
    }`,
    value: laneNumber,
  }));

  const hasOrderInLane = itemsInLane.some(
    (condition) => condition.orderInLane !== undefined || condition.orderInLanePriority !== undefined,
  );
  if (hasOrderInLane) {
    return options.concat([{ text: 'DETAILS_PANE.NO_ORDER', value: undefined }]);
  }

  return options;
}

/**
 * Calculate overlay values for all conditions in the same lane as the specified item
 */
export function calculateConditionOverlays(
  item: ConditionItem,
  overlay: number | undefined,
  allItems: ConditionItem[],
): ConditionLayoutUpdate[] {
  const otherConditionsInLane = allItems.filter(
    (condition) => condition.id !== item.id && condition.lane === item.lane,
  );
  const updates = [{ id: item.id, overlay, orderInLane: undefined, orderInLanePriority: undefined }];

  if (!overlay) {
    return updates.concat(
      otherConditionsInLane.map((condition) => ({
        id: condition.id,
        overlay: undefined,
        orderInLane: undefined,
        orderInLanePriority: undefined,
      })),
    );
  }

  const conditionInLaneWithSameOverlay = otherConditionsInLane.find((condition) => condition.overlay === overlay);
  const conditionsWithoutOverlay = otherConditionsInLane
    .filter((condition) => !condition.overlay)
    .concat(conditionInLaneWithSameOverlay ?? []);

  if (conditionsWithoutOverlay.length > 0) {
    const existingOverlays = [overlay].concat(
      otherConditionsInLane.filter((condition) => !!condition.overlay).map((condition) => condition.overlay!),
    );
    const possibleOverlays = _.range(1, otherConditionsInLane.length + 2).filter(
      (overlay) => !existingOverlays.includes(overlay),
    );
    conditionsWithoutOverlay.forEach((condition) => {
      updates.push({
        id: condition.id,
        overlay: possibleOverlays.shift(),
        orderInLane: undefined,
        orderInLanePriority: undefined,
      });
    });
  }

  return updates;
}

/**
 * Calculate order-in-lane values for all conditions in the same lane as the specified item.
 *
 * Unlike overlays (which simply avoid conflicts), order-in-lane represents display order and
 * uses insertion behavior: when an item moves to a new position, other items shift to
 * maintain contiguous ordering (1, 2, 3...).
 *
 * Example: If items are [a:1, b:2, c:3] and you move c to position 1,
 * the result is [c:1, a:2, b:3] - items a and b shift down to make room.
 */
export function calculateConditionOrderInLane(
  item: ConditionItem,
  newOrderInLane: number | undefined,
  allItems: ConditionItem[],
): ConditionLayoutUpdate[] {
  const orderInLaneConditions = allItems.filter((condition) => condition.lane === item.lane);
  const oldOrderInLane = item.orderInLane;

  if (newOrderInLane === undefined) {
    return orderInLaneConditions.map((condition) => createOrderInLaneUpdate(condition.id, undefined));
  }

  if (oldOrderInLane === undefined) {
    return assignInitialOrderInLane(item, newOrderInLane, orderInLaneConditions);
  }

  return reorderExistingOrderInLane(item, oldOrderInLane, newOrderInLane, orderInLaneConditions);
}

/**
 * Creates a standard update object for order-in-lane changes.
 * Centralizes the shape of updates to ensure consistency.
 * Clears orderInLanePriority since this is used for manual order-in-lane changes.
 */
function createOrderInLaneUpdate(id: string, orderInLane: number | undefined): ConditionLayoutUpdate {
  return {
    id,
    orderInLane,
    orderInLanePriority: undefined,
    overlay: undefined,
  };
}

/**
 * Assigns initial order-in-lane to a new item and handles other items in the lane.
 * When a new item joins with order-in-lane value N:
 * - Items with orderInLane >= N shift up by 1
 * - Items without orderInLane get assigned contiguous values
 */
function assignInitialOrderInLane(
  item: ConditionItem,
  newOrderInLane: number,
  orderInLaneConditions: ConditionItem[],
): ConditionLayoutUpdate[] {
  const result: ConditionLayoutUpdate[] = [];
  let nextSlot = 1;

  for (const condition of orderInLaneConditions) {
    if (condition.id === item.id) {
      result.push(createOrderInLaneUpdate(condition.id, newOrderInLane));
    } else {
      const order = condition.orderInLane;
      if (order !== undefined) {
        const adjustedOrderInLane = order >= newOrderInLane ? order + 1 : order;
        result.push(createOrderInLaneUpdate(condition.id, adjustedOrderInLane));
      } else {
        if (nextSlot === newOrderInLane) {
          nextSlot++;
        }
        result.push(createOrderInLaneUpdate(condition.id, nextSlot));
        nextSlot++;
      }
    }
  }

  return result;
}

/**
 * Reorders an existing item and shifts others to maintain contiguous order.
 * Example: Moving item from position 3 to 1 in [a:1, b:2, c:3]
 * - Items between new and old position shift (a:2, b:3, c:1)
 */
function reorderExistingOrderInLane(
  item: ConditionItem,
  oldOrderInLane: number,
  newOrderInLane: number,
  orderInLaneConditions: ConditionItem[],
): ConditionLayoutUpdate[] {
  return orderInLaneConditions.map((condition) => {
    const order = condition.orderInLane;
    let updatedOrderInLane = order;

    if (condition.id === item.id) {
      updatedOrderInLane = newOrderInLane;
    } else if (order !== undefined) {
      if (oldOrderInLane > newOrderInLane && order >= newOrderInLane && order < oldOrderInLane) {
        updatedOrderInLane = order + 1;
      } else if (oldOrderInLane < newOrderInLane && order > oldOrderInLane && order <= newOrderInLane) {
        updatedOrderInLane = order - 1;
      }
    }

    return createOrderInLaneUpdate(condition.id, updatedOrderInLane);
  });
}

/**
 * Calculate overlay and order-in-lane updates for a condition moving to a new lane.
 * When a condition moves to a new lane:
 * - Conditions remaining in oldLane get renumbered (1..N) while preserving their relative visual order
 * - Condition moving to newLane gets assigned next overlay/orderInLane value if newLane has them set
 * - Returns updates for ALL affected conditions in both lanes
 */
export function calculateConditionLaneChanges(
  item: ConditionItem,
  newLane: number,
  allItems: ConditionItem[],
): ConditionLayoutUpdate[] {
  const oldLane = item.lane;

  // No-op if selecting the same lane (avoid clearing overlay/orderInLane)
  if (oldLane === newLane) {
    return [];
  }

  const updates: ConditionLayoutUpdate[] = [];

  const newLaneConditions = allItems.filter((condition) => condition.lane === newLane);
  const newLaneHasOverlay = newLaneConditions.some((c) => c.overlay !== undefined);
  const newLaneHasOrderInLane = newLaneConditions.some((c) => c.orderInLane !== undefined);

  const movedItemUpdate: ConditionLayoutUpdate = {
    id: item.id,
    lane: newLane,
    overlay: undefined,
    orderInLane: undefined,
    orderInLanePriority: undefined,
  };

  if (newLaneHasOverlay) {
    const maxOverlay = Math.max(...newLaneConditions.map((c) => c.overlay || 0));
    movedItemUpdate.overlay = maxOverlay + 1;
  } else if (newLaneHasOrderInLane) {
    const maxOrderInLane = Math.max(...newLaneConditions.map((c) => c.orderInLane || 0));
    movedItemUpdate.orderInLane = maxOrderInLane + 1;
  }

  updates.push(movedItemUpdate);

  const oldLaneConditions = allItems.filter((condition) => condition.id !== item.id && condition.lane === oldLane);

  if (oldLaneConditions.length > 0) {
    const hasOverlay = oldLaneConditions.some((c) => c.overlay !== undefined);
    const hasOrderInLane = oldLaneConditions.some((c) => c.orderInLane !== undefined);

    if (hasOverlay) {
      // Sort by overlay value to preserve relative visual stacking order
      oldLaneConditions
        .sort((a, b) => (a.overlay ?? 0) - (b.overlay ?? 0))
        .forEach((condition, index) => {
          updates.push({
            id: condition.id,
            overlay: index + 1,
            orderInLane: undefined,
            ...(condition.orderInLanePriority !== undefined
              ? { orderInLanePriority: condition.orderInLanePriority }
              : { orderInLanePriority: undefined }),
          });
        });
    } else if (hasOrderInLane) {
      // Sort by orderInLanePriority (if present) then orderInLane to preserve relative display order
      oldLaneConditions
        .sort((a, b) => (a.orderInLanePriority ?? a.orderInLane ?? 0) - (b.orderInLanePriority ?? b.orderInLane ?? 0))
        .forEach((condition, index) => {
          updates.push({
            id: condition.id,
            orderInLane: index + 1,
            ...(condition.orderInLanePriority !== undefined
              ? { orderInLanePriority: condition.orderInLanePriority }
              : { orderInLanePriority: undefined }),
            overlay: undefined,
          });
        });
    }
  }

  return updates;
}

/**
 * Reindex overlay values for all conditions in a specific lane.
 * Conditions are sorted by overlay value and assigned contiguous 1-based values (1, 2, 3, ...)
 */
export function reindexOverlaysForLane(
  lane: number,
  setCustomizationProps: (items: WithProperty<{ id: string }>[]) => void,
  getAlignableItems: () => BaseItem[],
): void {
  const items = getAlignableItems() as BaseItem[];
  const conditionsInLane = items
    .filter((item) => item.itemType === ITEM_TYPES.CONDITION && item.lane === lane && item.overlay !== undefined)
    .sort((a, b) => (a.overlay ?? 0) - (b.overlay ?? 0));

  if (conditionsInLane.length === 0) return;

  const updates = conditionsInLane
    .map((item, index: number) => ({ id: item.id, overlay: index + 1 }))
    .filter(({ id, overlay }: { id: string; overlay: number }) => {
      const current = conditionsInLane.find((i) => i.id === id);
      return current?.overlay !== overlay;
    });

  if (updates.length > 0) {
    setCustomizationProps(updates);
  }
}

/**
 * Information about which lanes and reservations need reindexing after items are removed.
 */
export interface ReindexInfo {
  reservations: Set<string>;
  overlayLanes: Set<number>;
  orderInLaneLanes: Set<number>;
}

/**
 * Collect information about which lanes and reservations need reindexing.
 * Call this BEFORE removing items from the store, as it reads item properties.
 */
export function collectReindexInfoBeforeRemoval(
  items: BaseItem[],
  findItemInStores: (id: string) => BaseItem | undefined,
): ReindexInfo {
  const reservations = new Set<string>();
  const overlayLanes = new Set<number>();
  const orderInLaneLanes = new Set<number>();

  for (const item of items) {
    const storeItem = findItemInStores(item.id);
    const reservation = storeItem?.laneReservation ?? item.laneReservation;
    const orderInLane = storeItem?.orderInLane ?? item.orderInLane;
    const overlay = storeItem?.overlay ?? item.overlay;
    const lane = storeItem?.lane ?? item.lane;
    const itemType = storeItem?.itemType ?? item.itemType;

    if (reservation && orderInLane !== undefined && itemType === ITEM_TYPES.CONDITION) {
      reservations.add(reservation);
    }

    if (itemType === ITEM_TYPES.CONDITION && lane !== undefined) {
      if (overlay !== undefined) {
        overlayLanes.add(lane);
      }
      if (orderInLane !== undefined) {
        orderInLaneLanes.add(lane);
      }
    }
  }

  return { reservations, overlayLanes, orderInLaneLanes };
}

/**
 * Perform reindexing operations for overlays, order-in-lane, and reservations.
 * Call this AFTER removing items from the store, as it operates on remaining items.
 */
export function performReindexingAfterRemoval(
  reindexInfo: ReindexInfo,
  setCustomizationProps: (items: WithProperty<{ id: string }>[]) => void,
  getAlignableItems: () => BaseItem[],
): void {
  for (const reservation of reindexInfo.reservations) {
    reindexOrderInLaneForReservation(reservation, setCustomizationProps, getAlignableItems);
  }

  for (const lane of reindexInfo.overlayLanes) {
    reindexOverlaysForLane(lane, setCustomizationProps, getAlignableItems);
  }

  for (const lane of reindexInfo.orderInLaneLanes) {
    const laneConditions = getAlignableItems().filter(
      (item) => item.itemType === ITEM_TYPES.CONDITION && item.lane === lane,
    );
    const hasReservation = laneConditions.some((item) => item.laneReservation);
    if (!hasReservation) {
      reindexOrderInLaneForLane(lane, setCustomizationProps, getAlignableItems);
    }
  }
}

/**
 * Reindex orderInLane values for all conditions in a specific lane.
 * Conditions are sorted by orderInLane (or orderInLanePriority if available) and assigned contiguous 1-based values (1, 2, 3, ...)
 */
export function reindexOrderInLaneForLane(
  lane: number,
  setCustomizationProps: (items: WithProperty<{ id: string }>[]) => void,
  getAlignableItems: () => BaseItem[],
): void {
  const items = getAlignableItems() as BaseItem[];
  const conditionsInLane = items
    .filter(
      (item) =>
        item.itemType === ITEM_TYPES.CONDITION &&
        item.lane === lane &&
        (item.orderInLanePriority !== undefined || item.orderInLane !== undefined),
    )
    .sort((a, b) => (a.orderInLanePriority ?? a.orderInLane ?? 0) - (b.orderInLanePriority ?? b.orderInLane ?? 0));

  if (conditionsInLane.length === 0) return;

  const updates = conditionsInLane
    .map((item, index: number) => ({
      id: item.id,
      orderInLane: index + 1,
      ...(item.orderInLanePriority !== undefined ? { orderInLanePriority: item.orderInLanePriority } : {}),
    }))
    .filter(({ id, orderInLane }: { id: string; orderInLane: number }) => {
      const current = conditionsInLane.find((i) => i.id === id);
      return current?.orderInLane !== orderInLane;
    });

  if (updates.length > 0) {
    setCustomizationProps(updates);
  }
}
